import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AccountInfo {
  token: string;
  user: { id: string; name: string; email: string; role: string; dealership_id: string };
  dealership: { id: string; name: string; slug: string; tier: string; account_type: 'individual' | 'dealer' };
}

interface AuthState {
  token: string | null;
  user: AccountInfo['user'] | null;
  dealership: AccountInfo['dealership'] | null;
  accounts: AccountInfo[];
  deviceToken: string | null;
  setAuth: (token: string, user: AccountInfo['user'], dealership: AccountInfo['dealership']) => void;
  setAccounts: (accounts: AccountInfo[], deviceToken?: string) => void;
  switchAccount: (index: number) => void;
  logout: () => void;
  isDealer: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      dealership: null,
      accounts: [],
      deviceToken: null,

      setAuth: (token, user, dealership) => set({
        token, user, dealership,
        accounts: [{ token, user, dealership }],
      }),

      setAccounts: (accounts, deviceToken) => {
        if (!accounts.length) return;
        set({
          token: accounts[0].token,
          user: accounts[0].user,
          dealership: accounts[0].dealership,
          accounts,
          ...(deviceToken ? { deviceToken } : {}),
        });
      },

      switchAccount: (index) => {
        const accts = get().accounts;
        if (index < 0 || index >= accts.length) return;
        set({
          token: accts[index].token,
          user: accts[index].user,
          dealership: accts[index].dealership,
        });
      },

      logout: () => set({
        token: null, user: null, dealership: null,
        accounts: [], deviceToken: null,
      }),

      isDealer: () => (get().dealership?.account_type ?? 'dealer') === 'dealer',
    }),
    {
      name: 'cdos-auth',
      version: 3,
      migrate: (persisted: any, version: number) => {
        if (version < 2 && persisted?.dealership && !persisted.dealership.account_type) {
          persisted.dealership.account_type = 'dealer';
        }
        if (version < 3) {
          persisted.accounts = persisted.token ? [{
            token: persisted.token,
            user: persisted.user,
            dealership: persisted.dealership,
          }] : [];
          persisted.deviceToken = null;
        }
        return persisted;
      },
    }
  )
);
